import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { FullPageSpinner } from '@/components/Skeleton';

/**
 * FE-1-7 — where Entra redirects back to. Reads `code` and `state` from the query and
 * hands them to the backend, which redeems the code server-side (§5.1).
 *
 * Handles the cancelled-consent case explicitly: Entra returns
 * `error=access_denied` rather than a code, and that is a user decision, not a fault.
 */
export function MicrosoftCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { signInWithMicrosoft } = useAuth();
  const [message, setMessage] = useState('Completing sign-in…');
  // The state is single-use server-side, so a double effect run in StrictMode would
  // make the second attempt fail. Guard it.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    const code = params.get('code');
    const state = params.get('state');
    const entraError = params.get('error');
    const entraDescription = params.get('error_description');

    function bail(reason: string) {
      navigate(`/login?error=${encodeURIComponent(reason)}`, { replace: true });
    }

    if (entraError) {
      bail(
        entraError === 'access_denied'
          ? 'Sign-in was cancelled. You can try again when you are ready.'
          : (entraDescription ?? 'Microsoft sign-in could not be completed.'),
      );
      return;
    }

    if (!code || !state) {
      bail('That sign-in link was incomplete. Please try again.');
      return;
    }

    setMessage('Verifying your Microsoft account…');
    signInWithMicrosoft(code, state)
      .then((me) => navigate(me.landingPath, { replace: true }))
      .catch((cause: unknown) => {
        bail(
          cause instanceof ApiError
            ? cause.message
            : 'Microsoft sign-in could not be completed. Please try again.',
        );
      });
  }, [params, navigate, signInWithMicrosoft]);

  return <FullPageSpinner label={message} />;
}
